/**
 * API 签名生成工具
 * 为 passport 项目的 API 请求生成加密签名
 *
 * 加密算法说明:
 * 1. 使用 SHA256 处理密钥,取前32字节作为 AES-256 密钥
 * 2. 生成随机 IV(16字节)
 * 3. 使用 AES-CBC 模式 + PKCS7 填充进行加密
 * 4. 将 IV 和密文拼接后进行 Base64 编码(IV在前16字节)
 */

import CryptoJS from 'crypto-js';

// 环境类型映射
const typeEnum = {
  dev: 2,
  test: 1,
  uat: 2,
  prd: 3,
  sim: 4
};

// 获取当前环境类型
const env_type = typeEnum[process.env.VUE_APP_TYPE] || 3;

/**
 * 获取当前环境的加密密钥
 * @returns {string} 加密密钥
 */
function getEncryptionKey() {
  const encryptionKeys = {
    1: 'UAT#P(7qR8sT9+uV0—wX1yZ2!a',  // 测试环境
    2: 'UAT#P(7qR8sT9+uV0—wX1yZ2!a',  // UAT环境
    3: 'PRD#!aB4—cD5%eF6*gH7iJ8kL9',  // 正式环境
    4: 'SIM#8eF0%gH2*iJ4kL6@mN8oP0#'   // SIM环境
  };

  return encryptionKeys[env_type] || encryptionKeys[3];
}

/**
 * 生成当前时间戳(秒级)
 * @returns {number} 当前时间戳(秒)
 */
function generateTimestamp() {
  return Math.floor(Date.now() / 1000);
}

/**
 * AES 加密函数 - 完全匹配 C# AES 实现
 * @param {string} plaintext - 待加密的明文
 * @param {string} key - 加密密钥
 * @returns {string} Base64 编码的加密结果(IV+密文)
 */
function aesEncrypt(plaintext, key) {
  // 第一步: 使用 SHA256 处理密钥,取前32字节作为 AES-256 的密钥
  const keyHash = CryptoJS.SHA256(key);

  // 第二步: 生成随机 IV(16字节 = 128位)
  const iv = CryptoJS.lib.WordArray.random(16);

  // 第三步: 使用 AES-CBC 模式加密
  const encrypted = CryptoJS.AES.encrypt(plaintext, keyHash, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });

  // 第四步: 将 IV 和密文拼接
  // encrypted.ciphertext 只包含密文,不包含 IV
  // 我们需要手动将 IV 拼接到密文前面
  const combined = iv.clone().concat(encrypted.ciphertext);

  // 返回 Base64 编码的结果(IV在前16字节,后面是密文)
  return CryptoJS.enc.Base64.stringify(combined);
}

/**
 * AES 解密函数 - 完全匹配 C# AES 实现
 * @param {string} ciphertext - Base64 编码的密文(IV+密文)
 * @param {string} key - 解密密钥(与加密时使用的密钥相同)
 * @returns {string|null} 解密后的明文,失败时返回 null
 */
function aesDecrypt(ciphertext, key) {
  try {
    // 第一步: 使用 SHA256 处理密钥,取前32字节作为 AES-256 的密钥
    const keyHash = CryptoJS.SHA256(key);

    // 第二步: 将 Base64 密文转换为 WordArray
    const cipherBytes = CryptoJS.enc.Base64.parse(ciphertext);

    // 第三步: 从密文中提取 IV(前16字节)
    const iv = CryptoJS.lib.WordArray.create(cipherBytes.words.slice(0, 4), 16);

    // 第四步: 提取实际的密文(从第17字节开始)
    const actualCiphertext = CryptoJS.lib.WordArray.create(
      cipherBytes.words.slice(4),
      cipherBytes.sigBytes - 16
    );

    // 第五步: 使用 AES-CBC 模式解密
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: actualCiphertext },
      keyHash,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );

    // 第六步: 将解密结果转换为 UTF8 字符串
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('解密失败:', error);
    return null;
  }
}

/**
 * 生成 API 签名
 * @param {string} userIdentifier - 用户唯一标识(GID)
 * @param {string|number} userId - 用户ID,未登录时为 0
 * @returns {string} 加密签名
 */
export function generateSignature(userIdentifier, userId = 0) {
  // 生成时间戳
  const timestamp = generateTimestamp();

  // 构建待加密内容: 用户唯一标识|用户ID|时间戳
  const content = `${userIdentifier}|${userId}|${timestamp}`;

  // 获取加密密钥
  const encryptionKey = getEncryptionKey();

  // 生成签名
  const signature = aesEncrypt(content, encryptionKey);

  return signature;
}

/**
 * 获取 appkey
 * @returns {string} appkey 值
 */
export function getAppKey() {
  return 'passport';
}

// 导出供测试使用
export { generateTimestamp, aesEncrypt, aesDecrypt, getEncryptionKey };
